import React, { useState } from 'react';
import { Grid, Tooltip } from "@mui/material";
import {
    BreadCrumbs,
    PageTitleWithToolTip,
    AddButton,
    DataGrid,
    DeleteModal
} from "../../Common";
import toast from 'react-hot-toast';
import AddBusinessRuleModal from './AddBusinessRuleModal';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Routes from '../../../routes';
import useNavigate from '../../../hooks/useNavigate';
import { text_commonCellProps } from '../../Common/DataGrid/utils';
import StyledCellLink from '../../Common/DataGrid/CellComponents/StyledCellLink';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import { Grey2 } from '../../../color_utils';

// POC is shipping read-only for now: adding and deleting business rules are
// hidden. Flip this to false to restore the create/delete tooling.
const READ_ONLY = true;

const ManageBusinessRules = () => {
    const navigate = useNavigate({
        pageName: 'ManageBusinessRules'
    });
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [refreshNeeded, setRefreshNeeded] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const confirmDelete = async () => {
        try {
            const response = await fetch(Routes.BusinessRules.Delete(deleteTarget.id), {
                method: "DELETE",
                headers: { Accept: "application/json" },
            });

            if (response.status === 200) {
                toast.success(`Business rule "${deleteTarget.name}" deleted.`, { duration: 6000 });
                setRefreshNeeded(prev => !prev);
            } else if (response.status === 400) {
                const errors = await response.json();
                const messages = Array.isArray(errors)
                    ? errors.map(e => e.exceptionMessage).filter(Boolean).join('\n')
                    : '';
                toast.error(messages || 'There was an error deleting the business rule.', { duration: 8000 });
            } else {
                toast.error('There was an error deleting the business rule. Please try again.', { duration: 6000 });
            }
        } catch (error) {
            toast.error('There was an error deleting the business rule. Please try again.', { duration: 6000 });
        }
        setDeleteTarget(null);
    };

    const columns = [
        {
            field: 'id',
            headerName: 'ID',
            ...text_commonCellProps,
            renderCell: ({ value }) => (
                <StyledCellLink
                    primaryLink
                    path={`/BusinessRuleDetails/${value}`}
                    text={String(value)}
                    from={window.location.pathname}
                />
            ),
        },
        {
            field: 'name',
            headerName: 'Name',
            ...text_commonCellProps,
        },
        {
            field: 'type',
            headerName: 'Type',
            ...text_commonCellProps,
        },
        {
            field: 'subType',
            headerName: 'Sub Type',
            ...text_commonCellProps,
        },
        {
            field: 'updateDate',
            headerName: 'Last Updated',
            sortable: true,
            filterable: false,
            flex: 1,
            renderCell: ({ value }) => value ? new Date(value).toLocaleDateString() : '',
        },
        {
            field: 'Quick Actions',
            headerName: '',
            type: 'actions',
            width: 90,
            sortable: false,
            filterable: false,
            disableExport: true,
            getActions: (params) => [
                ...(READ_ONLY ? [] : [
                    <GridActionsCellItem
                        key="delete"
                        icon={
                            <Tooltip title="Delete business rule">
                                <DeleteOutlineIcon sx={{ color: Grey2 }} />
                            </Tooltip>
                        }
                        label="Delete"
                        onClick={() => setDeleteTarget({ id: params.id, name: params.row.name })}
                        showInMenu={false}
                    />,
                ]),
                <GridActionsCellItem
                    key="open"
                    icon={
                        <Tooltip title="Open ruleset">
                            <ChevronRightIcon sx={{ color: Grey2 }} />
                        </Tooltip>
                    }
                    label="Open"
                    onClick={() => navigate.push(`/BusinessRuleDetails/${params.id}`)}
                    showInMenu={false}
                />,
            ],
        }
    ];

    return (
        <>
            <BreadCrumbs
                path={[
                    { text: 'Geek Command Center', to: `/GeekCommandCenter` },
                    { text: 'Manage Business Rules' }
                ]}
            />
            <Grid
                container
                direction="row"
                alignItems="center"
            >
                <Grid item xs={6} md={6} lg={6} mb={2}>
                    <PageTitleWithToolTip
                        pageTitle="Manage Business Rules"
                        toolTipText={READ_ONLY
                            ? "View the business rule sets stored in the software. Select a ruleset to inspect its parsed rules in tabular or hierarchical form."
                            : "View and edit the business rule sets stored in the software. Select a ruleset to inspect its parsed rules, edit them, and save changes."}
                    />
                </Grid>
                {!READ_ONLY && (
                    <Grid item xs={6} md={6} lg={6} align="right">
                        <AddButton
                            text="Add business rule"
                            onClick={() => setAddModalOpen(true)}
                        />
                    </Grid>
                )}
            </Grid>
            <DataGrid
                refresh={refreshNeeded}
                showColumnHeaderFilter
                hideExportButton
                url={Routes.BusinessRules.GetTable()}
                noResultText="Oops... It's empty here..."
                noResultText2="No business rules matched your search criteria."
                columns={columns}
                getRowId={(row) => row.id}
            />

            <AddBusinessRuleModal
                modalOpen={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                onCreated={(businessRule) => {
                    setAddModalOpen(false);
                    navigate.push(`/BusinessRuleDetails/${businessRule.id}`);
                }}
            />

            <DeleteModal
                open={!!deleteTarget}
                title={`Delete business rule "${deleteTarget ? deleteTarget.name : ''}"?`}
                description="The rule and its whole ruleset are permanently removed and will no longer run. This cannot be undone from this screen."
                buttonText="Delete business rule"
                showImage={false}
                onConfirm={confirmDelete}
                onClose={() => setDeleteTarget(null)}
            />
        </>
    );
}

export default ManageBusinessRules;
